import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { gatewayWsUrlToHttpOrigin } from "../file-manager-embed.ts";
import { isUiDarkAppearance } from "../theme.ts";

const READY_TYPE = "openclaw-file-manager-ready";
const AUTH_TYPE = "openclaw-file-manager-auth";
const APPEARANCE_TYPE = "openclaw-file-manager-appearance";

@customElement("openclaw-file-manager-frame")
export class OpenClawFileManagerFrame extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
      min-width: 0;
    }
    iframe {
      flex: 1;
      min-height: 0;
      width: 100%;
      border: 0;
      border-radius: var(--radius-md, 8px);
      background: var(--bg-panel, var(--bg-content));
    }
  `;

  @property({ type: String }) embedUrl = "";
  @property({ type: String }) gatewayWsUrl = "";
  @property({ type: String }) token = "";
  @property({ type: String }) password = "";
  @property({ type: String }) themeMode = "system";
  @property({ type: String }) theme = "sand";

  private iframeReady = false;
  private boundOnMessage = this.onWindowMessage.bind(this);

  private getExpectedOrigin(): string | null {
    const httpOrigin = gatewayWsUrlToHttpOrigin(this.gatewayWsUrl);
    if (!httpOrigin) {
      return null;
    }
    try {
      return new URL(httpOrigin).origin;
    } catch {
      return null;
    }
  }

  private postAppearance(win: Window, expectedOrigin: string): void {
    win.postMessage(
      {
        type: APPEARANCE_TYPE,
        v: 1,
        dark: isUiDarkAppearance(this.theme, this.themeMode),
      },
      expectedOrigin,
    );
  }

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("message", this.boundOnMessage);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("message", this.boundOnMessage);
  }

  private onWindowMessage(ev: MessageEvent): void {
    const iframe = this.renderRoot.querySelector("iframe");
    if (!iframe || ev.source !== iframe.contentWindow) {
      return;
    }
    const data = ev.data as Record<string, unknown> | null;
    if (!data || typeof data !== "object" || data.type !== READY_TYPE) {
      return;
    }
    if (Number(data.v) !== 1) {
      return;
    }
    const expectedOrigin = this.getExpectedOrigin();
    if (!expectedOrigin) {
      return;
    }
    if (ev.origin !== expectedOrigin) {
      return;
    }
    const win = iframe.contentWindow;
    if (!win) {
      return;
    }
    this.iframeReady = true;
    win.postMessage(
      {
        type: AUTH_TYPE,
        v: 1,
        token: this.token || undefined,
        password: this.password || undefined,
        gatewayWsUrl: this.gatewayWsUrl,
      },
      expectedOrigin,
    );
    this.postAppearance(win, expectedOrigin);
  }

  override updated(changed: PropertyValues): void {
    super.updated(changed);
    if (changed.has("embedUrl")) {
      this.iframeReady = false;
    }
    if (!this.iframeReady || (!changed.has("themeMode") && !changed.has("theme"))) {
      return;
    }
    const iframe = this.renderRoot.querySelector("iframe");
    const win = iframe?.contentWindow;
    const expectedOrigin = this.getExpectedOrigin();
    if (!win || !expectedOrigin) {
      return;
    }
    this.postAppearance(win, expectedOrigin);
  }

  override render() {
    if (!this.embedUrl) {
      return nothing;
    }
    return html`
      <iframe
        class="file-manager-embed__frame"
        title="Workspace files"
        sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
        referrerpolicy="no-referrer"
        src=${this.embedUrl}
      ></iframe>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "openclaw-file-manager-frame": OpenClawFileManagerFrame;
  }
}
